import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Avatar} from 'antd';
import {
  StyledMainNavigation,
  StyledMainNavigationMenuIcon,
} from './StyledMainNavigation';
import Navigation from '../Navigation';
import {
  CoinsIcon,
  CreditCardRoundedIcon,
  ExpensesIcon,
  FolderFilledIcon,
  HandShakeIcon,
  HomeIcon,
  InvoiceIcon,
  NetSuiteIcon,
  TransactionOrderIcon
} from '../../icons';
import routesList from '../../routes/routes.json';
import {cardsHelpers, subscriptionsHelpers, textHelpers} from '../../utils/helpers';
import {
  availableModulesConstants, companyFeaturesConstants,
  subscriptionPaymentTypesConstants,
  subscriptionViewTypesConstants
} from '../../constants';
import {cardActions} from '../../state/actions/card.actions';
import {mainActions} from '../../state/actions/main.actions';
import {subscriptionActions} from '../../state/actions/subscription.actions';
import {
  useFeatureAvailable,
  useIsBankingEnabled,
  useIsEmployeeChanged,
  useIsEmployeeLoaded,
  useIsRequiredCardVerification,
  useIsTreezorEnabled
} from '../../hooks';

const {CARDS, TRANSACTIONS, SUBSCRIPTIONS} = availableModulesConstants;

const MainNavigation = ({
  dispatch,
  availableModules,
  isAdmin,
  isExistsBankingUser,
  navigationMenuProps,
  getCards,
  getSubscriptions,
  setNavigationLoading,
  setNavigationMenuProps,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [userCards, setUserCards] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [navigationProps, setNavigationProps] = useState({items: []});

  const isEmployeeLoaded = useIsEmployeeLoaded();

  const isEmployeeChanged = useIsEmployeeChanged();

  const isAccountingAvailable = useFeatureAvailable(companyFeaturesConstants.ACCOUNTING_MODULE);
  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const isTreezorEnabled = useIsTreezorEnabled();
  const isBankingEnabled = useIsBankingEnabled();

  const {
    checked: isCheckedBankingDetails,
    required: isRequiredCardVerification
  } = useIsRequiredCardVerification();

  const isDisabledModule = (name) => !availableModules.includes(name);

  const finishLoading = () => setLoading(false);

  const storeNavigationProps = (items = []) => {
    setNavigationProps({items});
    setNavigationMenuProps({items: items.map(({ icon, ...rest }) => rest)});
  }

  const formatNavigationItem = (item) => ({
    className: item.className || undefined,
    children: item.children ? item.children.map(i => formatNavigationItem(i)) : undefined,
    dividedKey: item.dividedKey,
    disabled: item.disabled,
    icon: item.icon,
    key: item.title,
    label: t(`navigation.${item.title}`),
    path: item.path
  });

  const loadCards = () => {
    getCards(
      undefined,
      (response) => {
        setUserCards(response?.results || []);
        finishLoading();
      },
      finishLoading
    );
  }

  const loadSubscriptions = () => {
    getSubscriptions(
      {view: subscriptionViewTypesConstants.AUTHORIZED},
      (response) => {
        setUserSubscriptions(response?.subscriptions || []);
        finishLoading();
      },
      finishLoading
    );
  }

  const navigationItems = useMemo(() => {
    let items = [...navigationProps.items];
    if (!isAdmin) {
      items = [
        formatNavigationItem({
          icon: <HomeIcon />,
          path: routesList.overview,
          title: 'overview'
        }),
        isBudgetsAvailable && formatNavigationItem({
          children: [
            { path: routesList.budgetsActive, title: 'budgetsActive' },
            { path: routesList.budgetsRequests, title: 'budgetsRequests' },
          ],
          icon: <CoinsIcon />,
          title: 'budget'
        }),
        formatNavigationItem({
          icon: <ExpensesIcon />,
          path: routesList.expensesList,
          title: 'expenses'
        }),
        ...items,
      ]
    }
    return items.filter(Boolean);
  }, [isAdmin, isExistsBankingUser, navigationProps.items, isBudgetsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isTreezorEnabled) setLoading(true);
  }, [isEmployeeChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmployeeLoaded && !isAdmin && isBankingEnabled) {
      setLoading(true);
      loadCards();
      loadSubscriptions();
    } else {
      setLoading(false);
      setNavigationLoading(false);
    }
  }, [isEmployeeLoaded, isBankingEnabled, isRequiredCardVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const disabledTransactions = isBankingEnabled ? isDisabledModule(TRANSACTIONS) : true;
    let items = [];
    if (isAdmin) {
      items = [
        {
          icon: <HomeIcon />,
          path: routesList.overview,
          title: 'overview'
        },
        {
          available: isBudgetsAvailable,
          children: [
            { path: routesList.budgetsActive, title: 'budgetsActive' },
            { path: routesList.budgetsRequests, title: 'budgetsRequests' },
          ],
          icon: <CoinsIcon />,
          title: 'budget'
        },
        {
          icon: <HandShakeIcon />,
          path: routesList.vendorsList,
          title: 'vendors'
        },
        {
          icon: <FolderFilledIcon />,
          path: routesList.subscriptionsList,
          title: 'subscriptions',
        },
        {
          icon: <ExpensesIcon />,
          path: routesList.expensesList,
          title: 'expenses'
        },
        {
          available: isTreezorEnabled,
          disabled: isDisabledModule(CARDS),
          icon: <CreditCardRoundedIcon />,
          path: routesList.cardsList,
          title: 'cards',
        },
        {
          available: isTreezorEnabled,
          disabled: disabledTransactions,
          icon: <TransactionOrderIcon />,
          path: routesList.transactionsList,
          title: 'account'
        },
        {
          dividedKey: 'accounting',
          icon: <InvoiceIcon />,
          path: routesList.invoicesList,
          title: 'invoices'
        },
        {
          available: isAdmin && isAccountingAvailable,
          className: 'netsuite-menu-item',
          children: [
            { path: routesList.netSuiteVendors, title: 'netsuiteVendors' },
            { path: routesList.netSuiteVendorBills, title: 'vendorBills' },
            { path: routesList.netSuiteJournalEntriesList, title: 'journalEntries' }
          ],
          dividedKey: 'accounting',
          icon: <NetSuiteIcon />,
          title: 'netsuite'
        }
      ];
      setLoading(false);

      // clear previous state is needed
      userCards.length && setUserCards([]);
      userSubscriptions.length && setUserSubscriptions([]);

      // show only available features
      items = items
        .filter(i => i.available !== false)
        .map(i => formatNavigationItem(i));
      storeNavigationProps(items);
    }
  }, [isAdmin, t, isRequiredCardVerification, availableModules, isAccountingAvailable, isTreezorEnabled, isBudgetsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let items = [];
    if (!isAdmin) {
      let id, label, logo, key, ownerId;
      const isDisabledSubscriptionPage = isDisabledModule(SUBSCRIPTIONS) || isRequiredCardVerification;
      const isDisabledCardPage = isDisabledModule(CARDS) || isRequiredCardVerification;
      // create menu array from subscriptions
      const subscriptionsItems = userSubscriptions.map(subscription => {
        id = subscription.id;
        key = `subscription-${id}`;
        label = subscriptionsHelpers.getSubscriptionName(subscription);
        logo = subscription?.service?.logo || undefined;
        return {
          dividedKey: 'subscriptions',
          disabled: isDisabledSubscriptionPage && subscription.payment_type === subscriptionPaymentTypesConstants.CARD,
          icon: (
            <Avatar src={logo} size={18}>
              {textHelpers.getInitials(label)}
            </Avatar>
          ),
          key,
          label,
          path: `${routesList.subscriptionsList}/${id}`,
          variant: 'subscription'
        }
      }).sort((a, b) => a.label.localeCompare(b.label));

      // create menu array from cards
      if (isTreezorEnabled && userCards.length) {
        userCards.forEach((card) => {
          id = card.card_id;
          key = `card-${id}`;
          ownerId = card?.owner?.id;
          items.push({
            disabled: isDisabledCardPage,
            dividedKey: 'cards',
            icon: (
              <StyledMainNavigationMenuIcon>
                {cardsHelpers.getCardIcon(card)}
              </StyledMainNavigationMenuIcon>
            ),
            key,
            label: `${card?.masked_pan ? '**' : ''}${cardsHelpers.getLast4Digits(card?.masked_pan)}`,
            path: ownerId && `${routesList.cardsList}/${ownerId}/${id}`,
            variant: 'card'
          });
        });
      }

      // finish loading if banking details is checked
      if (isCheckedBankingDetails) {
        finishLoading();
        setNavigationLoading(false);
      }

      subscriptionsItems.forEach(item => {
        items.push(item);
      });

      storeNavigationProps(items);
    }
  }, [isCheckedBankingDetails, userCards, userSubscriptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledMainNavigation {...rest}>
      <Navigation
        items={navigationItems}
        loading={loading}
      />
    </StyledMainNavigation>
  )
}

const mapStateToProps = state => {
  const {banking, main, user} = state;

  return {
    availableModules: main.availableModules?.modules || [],
    isAdmin: user.isAdmin,
    isExistsBankingUser: banking?.verification?.isCreatedUser,
    navigationMenuProps: main?.navigationMenuProps
  }
}

const mapDispatchToProps = {
  getCards: cardActions.getCards,
  getSubscriptions: subscriptionActions.getSubscriptionsList,
  setNavigationLoading: mainActions.setNavigationLoading,
  setNavigationMenuProps: mainActions.setNavigationMenuProps
}

export default connect(mapStateToProps, mapDispatchToProps)(MainNavigation);
